/**
 * Snapshot of the tutor's playback state, taken when the student presses to
 * talk. Canonical explanation of why this exists — other sites point here.
 *
 * The backend decides whether to truncate the interrupted assistant message
 * from the `is_interrupt` flag on TURN_CHANGE, so that flag has to mean "the
 * tutor was speaking when the student pressed". Reading the live PCM player
 * any later cannot answer that, because two independent things clear the
 * player around a press:
 *
 *   - useAudioPlayer subscribes to TURN_CHANGE and clears the player. Whether
 *     it runs before or after useWebsocketClient's own subscriber is
 *     incidental: that effect re-registers on every RuntimeContext re-render
 *     and drifts to the end of the listener list.
 *   - handleInterrupt() clears the player directly, and the pending-work /
 *     locked-ink press path calls it before startRecording.
 *
 * Either one leaves isPlaying() false by the time the flag is computed, the
 * backend skips _handle_partial_response, and the conversation history keeps
 * text the tutor never spoke.
 *
 * `wasSlideChangePending` is the same hazard for the next-slide countdown,
 * which outlives the audio and is cleared by cancelAllNotifications(). Once it
 * ends the change is committed and no longer counts. A change whose
 * change_slide then failed is not an interrupt either: it rides TURN_CHANGE as
 * its own field (hasFailedAiSlideChange in useWebsocketClient.js).
 *
 * Pass the result to startRecording() when the caller stops playback or cancels
 * notifications before recording begins; otherwise startRecording() captures it
 * itself.
 */
import { useNotificationStore } from "@/store/useNotificationStore";

/**
 * Input:  none — reads the countdown.
 * Output: true while a tutor-announced slide change can still be called off.
 */
export function isSlideChangePending() {
  return useNotificationStore.getState().slideChangeCountdownCount > 0;
}

export function buildTutorSpeechSnapshot() {
  return {
    wasTutorSpeaking: window.pcmPlayer?.isPlaying() ?? false,
    wasSlideChangePending: isSlideChangePending(),
    // Undefined rather than null when absent: the consumer falls back to the
    // live global with `??`, and null would defeat a snapshot that says
    // "audio had not started yet".
    audioStartMs: window.__tutorAudioStartWallClockMs,
  };
}
